import { ClockCircleOutlined } from '@ant-design/icons'
import { useAuthenticator } from '@aws-amplify/ui-react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { authTokenCache } from '../../lib/auth-token-cache'

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

interface TokenInfo {
  token: string
  expiresAt: number
  issuedAt: number
  timeLeft: number
}

interface JWTPayload {
  iat?: number
  exp?: number
  [key: string]: unknown
}

// ============================================================================
// AMPLIFY SESSION MONITOR CLASS
// ============================================================================

class AmplifySessionMonitor {
  private lastToken: string | null = null
  private sessionCheckInterval: NodeJS.Timeout | null = null

  constructor() {
    this.startSessionMonitoring()
  }

  public async logSessionEvent(event: string): Promise<void> {
    try {
      const session = await fetchAuthSession()
      const accessToken = session.tokens?.accessToken?.toString()

      if (!accessToken) {
        console.info(`🔍 [AUTH_MONITOR] ${event} - No access token available`)
        return
      }

      const tokenInfo = this.parseTokenInfo(accessToken)
      const tokenPreview = `${accessToken.slice(0, 5)}...${accessToken.slice(-5)}`
      const expiresAt = new Date(tokenInfo.expiresAt * 1000).toISOString()

      console.info(
        `🔍 [AUTH_MONITOR] ${event}, ${tokenPreview}, ${this.formatTimeLeft(tokenInfo.timeLeft)}, ${expiresAt}`
      )

      if (this.lastToken && this.lastToken !== accessToken) {
        console.info(
          `🔄 [AUTH_MONITOR] TOKEN_CHANGED, ${tokenPreview}, ${this.formatTimeLeft(tokenInfo.timeLeft)}, ${expiresAt}`
        )
      }

      this.lastToken = accessToken
    } catch (error: unknown) {
      console.error(`❌ [AUTH_MONITOR] Error logging session event ${event}:`, error)
    }
  }

  private parseTokenInfo(token: string): TokenInfo {
    try {
      const parts = token.split('.')
      if (parts.length !== 3 || !parts[1]) {
        throw new Error('Invalid token format')
      }
      const payload = JSON.parse(atob(parts[1])) as JWTPayload
      const issuedAt = payload.iat ?? 0
      const expiresAt = payload.exp ?? 0
      const currentTime = Math.floor(Date.now() / 1000)
      const timeLeft = expiresAt - currentTime

      return {
        token,
        expiresAt,
        issuedAt,
        timeLeft
      }
    } catch (error: unknown) {
      console.error('❌ [AUTH_MONITOR] Error parsing token:', error)
      return {
        token,
        expiresAt: 0,
        issuedAt: 0,
        timeLeft: 0
      }
    }
  }

  private formatTimeLeft(seconds: number): string {
    if (seconds <= 0) return 'EXPIRED'

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    } else {
      return `${secs}s`
    }
  }

  private startSessionMonitoring(): void {
    this.sessionCheckInterval = setInterval(() => {
      void (async () => {
        try {
          const session = await fetchAuthSession()
          const accessToken = session.tokens?.accessToken?.toString()

          if (accessToken) {
            const tokenInfo = this.parseTokenInfo(accessToken)

            if (tokenInfo.timeLeft < 300 && tokenInfo.timeLeft > 0) {
              console.info(
                `⚠️ [AUTH_MONITOR] TOKEN_EXPIRING_SOON, ${accessToken.slice(0, 5)}...${accessToken.slice(-5)}, ${this.formatTimeLeft(tokenInfo.timeLeft)}, ${new Date(tokenInfo.expiresAt * 1000).toISOString()}`
              )
            }

            if (tokenInfo.timeLeft <= 0) {
              console.info(
                `🚨 [AUTH_MONITOR] TOKEN_EXPIRED, ${accessToken.slice(0, 5)}...${accessToken.slice(-5)}, EXPIRED, ${new Date(tokenInfo.expiresAt * 1000).toISOString()}`
              )
            }
          }
        } catch (error: unknown) {
          console.error('❌ [AUTH_MONITOR] Error in session monitoring:', error)
        }
      })()
    }, 30000)
  }

  public destroy(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval)
      this.sessionCheckInterval = null
    }
  }
}

// ============================================================================
// GLOBAL MONITOR INSTANCE AND UTILITIES
// ============================================================================

let sessionMonitor: AmplifySessionMonitor | null = null

export const getSessionMonitor = (): AmplifySessionMonitor | null => {
  return sessionMonitor
}

export const createSessionMonitor = (): AmplifySessionMonitor => {
  if (!sessionMonitor) {
    sessionMonitor = new AmplifySessionMonitor()
  }
  return sessionMonitor
}

export const destroySessionMonitor = (): void => {
  if (sessionMonitor) {
    sessionMonitor.destroy()
    sessionMonitor = null
  }
}

export const logCurrentToken = async (event = 'MANUAL_CHECK'): Promise<void> => {
  if (sessionMonitor) {
    await sessionMonitor.logSessionEvent(event)
  } else {
    console.info('🔍 [AUTH_MONITOR] No session monitor available')
  }
}

// ============================================================================
// SESSION TIMEOUT TRACKING
// ============================================================================

// ============================================================================
// SESSION CONFIGURATION - Easy to adjust for production
// ============================================================================

const SESSION_TIMEOUT_KEY = 'payments_session_timeout'
const LAST_ACTIVITY_KEY = 'payments_last_activity'
const LOGOUT_SIGNAL_KEY = 'payments_logout_signal'
const AUTH_STATE_KEY = 'payments_auth_state'

// Session Configuration - 1 hour timeout with 55 minute warning for all environments
const IDLE_TIMEOUT_MS = 60 * 60 * 1000 // 1 hour for all environments
const WARNING_TIMEOUT_MS = 55 * 60 * 1000 // 55 minutes for all environments (5 min warning)

const updateLastActivity = (): void => {
  const now = Date.now()
  const timeoutTimestamp = now + IDLE_TIMEOUT_MS

  localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
  localStorage.setItem(SESSION_TIMEOUT_KEY, timeoutTimestamp.toString())

  // console.info(`🕐 [SESSION] Updated activity at ${new Date(now).toISOString()}, expires at ${new Date(timeoutTimestamp).toISOString()}`)
}

const checkSessionExpiry = (): boolean => {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
  const sessionTimeout = localStorage.getItem(SESSION_TIMEOUT_KEY)

  if (!lastActivity || !sessionTimeout) {
    console.info('🕐 [SESSION] No previous session data found')

    return false
  }

  const now = Date.now()
  const timeoutTimestamp = Number.parseInt(sessionTimeout, 10)
  const isExpired = now > timeoutTimestamp

  // console.info(`🕐 [SESSION] Check: now=${new Date(now).toISOString()}, timeout=${new Date(timeoutTimestamp).toISOString()}, expired=${String(isExpired)}`)

  return isExpired
}

const clearSessionData = (): void => {
  localStorage.removeItem(LAST_ACTIVITY_KEY)
  localStorage.removeItem(SESSION_TIMEOUT_KEY)
}

// ============================================================================
// CROSS-TAB AUTH STATE MANAGEMENT
// ============================================================================

const handleCrossTabAuthDetection = (): void => {
  console.info('🔄 [CROSS_TAB_AUTH] Signaling auth state change to other tabs')
  const timestamp = Date.now().toString()
  localStorage.setItem(AUTH_STATE_KEY, timestamp)
}

// ============================================================================
// MAIN AUTH LISTENER COMPONENT
// ============================================================================

export const AuthListener = (): JSX.Element | null => {
  const { authStatus, signOut: amplifySignOut, user } = useAuthenticator()
  const [showIdleWarning, setShowIdleWarning] = useState(false)
  const [sessionChecked, setSessionChecked] = useState(false)
  const [authSignalSent, setAuthSignalSent] = useState(false)

  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.50)',
      backdropFilter: 'blur(0px)',
      WebkitBackdropFilter: 'blur(0px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '200px',
      paddingBottom: '20px',
      animation: 'overlay-fade-in 0.5s forwards ease'
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: '24px',
      padding: '48px 40px',
      width: '480px',
      boxShadow: '0 20px 48px rgba(0, 0, 0, 0.18)',
      textAlign: 'center' as const,
      fontFamily: 'Inter, sans-serif',
      opacity: 0,
      transform: 'translateY(20px)',
      animation: 'modal-fade-in 0.4s 0.1s forwards ease'
    },
    iconContainer: {
      marginBottom: '24px'
    },
    icon: {
      fontSize: '48px',
      color: '#1890ff',
      animation: 'icon-pulse 1.5s infinite ease-in-out'
    },
    title: {
      fontSize: '20px',
      fontWeight: 600,
      color: '#111',
      marginBottom: '26px'
    },
    message: {
      fontSize: '16px',
      color: '#444',
      marginBottom: '20px'
    },
    subtext: {
      fontSize: '15px',
      fontWeight: 500,
      color: '#888'
    }
  }

  useEffect(() => {
    if (authStatus === 'authenticated' && user && !sessionChecked) {
      // console.info('🕐 Checking session expiry on authentication...')

      const isExpired = checkSessionExpiry()

      if (isExpired) {
        console.info('🚨 Session expired - forcing logout')
        clearSessionData()
        amplifySignOut()
        return
      }

      // console.info('✅ Session valid - continuing authentication')
      setSessionChecked(true)
      updateLastActivity()

      if (!authSignalSent) {
        console.info('🔄 [CROSS_TAB_AUTH] First authentication - signaling to other tabs')
        handleCrossTabAuthDetection()
        setAuthSignalSent(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, user, sessionChecked, amplifySignOut])

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      console.info('👋 User signed out, cleaning up Redux state')
      clearSessionData()
      setSessionChecked(false)
      setAuthSignalSent(false) // Reset signal flag for next authentication
    }
  }, [authStatus, user, sessionChecked])

  useEffect(() => {
    if (authStatus === 'authenticated') {
      createSessionMonitor()

      void fetchAuthSession()
        .then(session => {
          const token = session.tokens?.accessToken?.toString()
          if (token) {
            localStorage.setItem('token', token)
            void logCurrentToken('AUTHENTICATED_INITIAL')
            setTimeout(() => {
              const currentToken = localStorage.getItem('token')
              if (currentToken) {
                void logCurrentToken('TOKEN_VERIFIED_AVAILABLE')
              } else {
                void logCurrentToken('TOKEN_NOT_FOUND_IN_STORAGE')
              }
            }, 500)
          } else {
            void logCurrentToken('NO_TOKEN_AVAILABLE_AFTER_AUTH')
          }
        })
        .catch((error: unknown) => {
          console.error('❌ [AUTH_MONITOR] Error fetching auth session:', error)
          void logCurrentToken('AUTH_SESSION_FETCH_ERROR')
        })
    } else if (authStatus === 'unauthenticated') {
      destroySessionMonitor()
      void logCurrentToken('USER_SIGNED_OUT')
    }
  }, [authStatus])

  useEffect(() => {
    const handleTokenRefresh = async (): Promise<void> => {
      try {
        const session = await fetchAuthSession()
        const token = session.tokens?.accessToken?.toString()
        if (token) {
          localStorage.setItem('token', token)
          void logCurrentToken('TOKEN_REFRESHED')
        }
      } catch (error: unknown) {
        console.error('❌ [AUTH_MONITOR] Error handling token refresh:', error)
      }
    }

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'token' && e.newValue) {
        void logCurrentToken('TOKEN_UPDATED_VIA_STORAGE')

        location.reload()
        return
      }

      if (e.key === 'token' && !e.newValue && authStatus === 'authenticated') {
        console.info('🚨 [CROSS_TAB_LOGOUT] Token removed in another tab - forcing logout')
        void logCurrentToken('TOKEN_REMOVED_CROSS_TAB')
        authTokenCache.handleCrossTabLogout()
        clearSessionData()
        amplifySignOut()
        return
      }

      if (e.key === LOGOUT_SIGNAL_KEY && e.newValue && authStatus === 'authenticated') {
        console.info('🚨 [CROSS_TAB_LOGOUT] Logout signal received from another tab')
        void logCurrentToken('LOGOUT_SIGNAL_RECEIVED')
        authTokenCache.handleCrossTabLogout()
        clearSessionData()
        localStorage.removeItem(LOGOUT_SIGNAL_KEY)
        amplifySignOut()
        return
      }

      if (e.key === AUTH_STATE_KEY && e.newValue && authStatus === 'unauthenticated') {
        console.info('🔄 [CROSS_TAB_AUTH] Auth state change detected from another tab')
        void logCurrentToken('AUTH_STATE_CHANGE_DETECTED')
      }
    }

    const handleSessionExpiryCheck = (): void => {
      if (authStatus === 'authenticated' && sessionChecked) {
        const isExpired = checkSessionExpiry()
        if (isExpired) {
          console.info('🚨 Periodic check: Session expired - forcing logout')

          console.info('🚨 [SESSION_EXPIRY] Sending cross-tab logout signal due to session expiry')
          localStorage.setItem('payments_logout_signal', Date.now().toString())
          localStorage.removeItem('token')

          clearSessionData()
          setAuthSignalSent(false) // Reset signal flag for next authentication after session expiry
          amplifySignOut()
        }
      }
    }

    const handleVisibilityChange = (): void => {
      if (
        document.visibilityState === 'visible' &&
        authStatus === 'authenticated' &&
        sessionChecked
      ) {
        // console.info('🔍 Tab became visible - checking session expiry')
        const isExpired = checkSessionExpiry()
        if (isExpired) {
          console.info('🚨 Visibility change: Session expired - forcing logout')

          console.info(
            '🚨 [VISIBILITY_EXPIRY] Sending cross-tab logout signal due to visibility check expiry'
          )
          localStorage.setItem('payments_logout_signal', Date.now().toString())
          localStorage.removeItem('token')

          clearSessionData()
          setAuthSignalSent(false) // Reset signal flag for next authentication after visibility check expiry
          amplifySignOut()
        } else {
          updateLastActivity()
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    const refreshCheckInterval = setInterval(() => {
      void handleTokenRefresh()
    }, 60000)

    const sessionCheckInterval = setInterval(handleSessionExpiryCheck, 5000)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearInterval(refreshCheckInterval)
      clearInterval(sessionCheckInterval)
    }
  }, [authStatus, sessionChecked, amplifySignOut])

  const handleIdle = (): void => {
    setShowIdleWarning(false)
    void logCurrentToken('USER_IDLE_TIMEOUT')

    console.info('🚨 [IDLE_TIMEOUT] Sending cross-tab logout signal due to idle timeout')
    localStorage.setItem('payments_logout_signal', Date.now().toString())
    localStorage.removeItem('token')

    clearSessionData()
    setAuthSignalSent(false) // Reset signal flag for next authentication after idle timeout
    amplifySignOut()
  }

  const handleActive = (): void => {
    setShowIdleWarning(false)
    updateLastActivity()
    void logCurrentToken('USER_ACTIVE')
  }

  const handleAction = (): void => {
    setShowIdleWarning(false)
    updateLastActivity()
    void logCurrentToken('USER_ACTION')
  }

  const handleIdleWarning = (): void => {
    setShowIdleWarning(true)
    void logCurrentToken('IDLE_WARNING_SHOWN')
  }

  useIdleTimer({
    onIdle: handleIdle,
    onActive: handleActive,
    onAction: handleAction,
    timeout: IDLE_TIMEOUT_MS,
    throttle: 500
  })

  useIdleTimer({
    onIdle: handleIdleWarning,
    timeout: WARNING_TIMEOUT_MS,
    throttle: 500
  })

  if (showIdleWarning) {
    return (
      <>
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.iconContainer}>
              <ClockCircleOutlined style={styles.icon} />
            </div>
            <h2 style={styles.title}>You&apos;re about to be logged out</h2>
            <div style={styles.message}>
              For security reasons, you will be automatically signed out in 5 minutes due to
              inactivity.
            </div>
            <div style={styles.subtext}>Move your mouse or press any key to remain logged in.</div>
          </div>
        </div>
        <style>
          {`
  @keyframes icon-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.15); }
  }

  @keyframes overlay-fade-in {
    0% {
      background-color: rgba(0, 0, 0, 0);
      backdrop-filter: blur(0px);
    }
    100% {
      background-color: rgba(0, 0, 0, 0.25);
      backdrop-filter: blur(8px);
    }
  }

  @keyframes modal-fade-in {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
`}
        </style>
      </>
    )
  }

  return null
}
